import React, { useEffect, useState } from 'react';
import {
    AppBar, Toolbar, Typography, Box, Button, Chip, Drawer, List, ListItemButton,
    ListItemIcon, ListItemText, IconButton, Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TuneIcon from '@mui/icons-material/Tune';
import CategoryIcon from '@mui/icons-material/Category';
import HistoryIcon from '@mui/icons-material/History';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LogoutIcon from '@mui/icons-material/Logout';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GroupIcon from '@mui/icons-material/Group';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PromoCodesPanel } from 'promo-codes-ui';
import BrandIcon from '../Components/BrandIcon';
import LemonCoinPricePanel from '../Components/LemonCoinPricePanel';
import PlatformFeePanel from '../Components/PlatformFeePanel';
import CoinGrantsPanel from '../Components/CoinGrantsPanel';
import TransactionsPanel from '../Components/TransactionsPanel';
import ServicePricingPanel from '../Components/ServicePricingPanel';
import CategoriesServicesPanel from '../Components/CategoriesServicesPanel';
import ActivityLogPanel from '../Components/ActivityLogPanel';
import LeadsPanel from '../Components/LeadsPanel';
import UsersPanel from '../Components/UsersPanel';
import CatalogPanel from '../Components/CatalogPanel';
import ConfigurationPanel from '../Components/ConfigurationPanel';
import AlertsBell from '../Components/AlertsBell';
import { catalogItemFromLink } from '../Services/alertsModel';
import httpClient from '../Services/httpClient';
import { canSeeCatalog, getCurrentUsername, isUserAdmin, logout, setToken } from '../Services/AuthService';

const DRAWER_WIDTH_OPEN = 260;
const DRAWER_WIDTH_CLOSED = 72;

// ACTIVITY LOG deve ficar sempre por último, não importa o que for adicionado depois
// (é uma visão de auditoria de tudo, não um fluxo de trabalho primário) — ver memória do projeto.
//
// `visible` decide só o que MOSTRAR por perfil; quem barra de verdade é o BFF (políticas) e o core-backoffice (mesmo token).
// As telas antigas (preços, estorno, categorias, cupons, carteira, leads, auditoria) são da administração da plataforma: perfil
// de administrador de usuários. Catálogo e usuários têm os seus perfis.
const MENU = [
    {
        label: 'LEMON COIN', icon: <PaidIcon />, visible: isUserAdmin,
        render: () => (
            <>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <LemonCoinPricePanel />
                    <PlatformFeePanel />
                </Box>
                <CoinGrantsPanel />
            </>
        )
    },
    { label: 'TRANSAÇÕES', icon: <ReceiptLongIcon />, visible: isUserAdmin, render: () => <TransactionsPanel /> },
    { label: 'PREÇOS FREEMIUM', icon: <TuneIcon />, visible: isUserAdmin, render: () => <ServicePricingPanel /> },
    { label: 'CATEGORIAS E SERVIÇOS', icon: <CategoryIcon />, visible: isUserAdmin, render: () => <CategoriesServicesPanel /> },
    { label: 'CÓDIGOS PROMOCIONAIS', icon: <LocalOfferIcon />, visible: isUserAdmin, render: () => <PromoCodesPanel httpClient={httpClient} /> },
    { label: 'LEADS', icon: <PsychologyIcon />, visible: isUserAdmin, render: () => <LeadsPanel /> },
    // O Dashboard passa o item a abrir (vindo de um aviso do sino ou de um link `/?catalogo=<id>`) por `context`.
    { label: 'NOTIFICAÇÕES', icon: <NotificationsActiveIcon />, visible: canSeeCatalog, render: (context) => <CatalogPanel initialItemId={context.catalogItemId} onInitialItemConsumed={context.clearCatalogItem} /> },
    { label: 'CONFIGURAÇÃO', icon: <SettingsIcon />, visible: canSeeCatalog, render: () => <ConfigurationPanel /> },
    { label: 'USUÁRIOS', icon: <GroupIcon />, visible: isUserAdmin, render: () => <UsersPanel /> },
    { label: 'ACTIVITY LOG', icon: <HistoryIcon />, visible: isUserAdmin, render: () => <ActivityLogPanel /> }
];

const KEEP_ALIVE_INTERVAL_MS = 15 * 60 * 1000;

const Dashboard = () => {
    const navigate = useNavigate();
    const [selected, setSelected] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const [catalogItemId, setCatalogItemId] = useState(null);
    // Os itens que este usuário pode ver (o perfil vem do token da sessão).
    const items = MENU.filter((item) => item.visible());
    const [drawerOpen, setDrawerOpen] = useState(true);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const response = await httpClient.post('/auth/refresh');
                setToken(response.data.token);
            } catch {
                // Falha aqui não precisa de tratamento especial — o interceptor de 401 do
                // httpClient já cuida de redirecionar pro /login se o token atual tiver vencido.
            }
        }, KEEP_ALIVE_INTERVAL_MS);

        return () => clearInterval(interval);
    }, []);

    // Abre um item do catálogo: vem de um aviso do sino ou do link `/?catalogo=<id>`. Só para quem vê o catálogo.
    const openCatalogItem = (itemId) => {
        const index = MENU.filter((item) => item.visible()).findIndex((item) => item.label === 'NOTIFICAÇÕES');
        if (index < 0 || !itemId) return;
        setSelected(index);
        setCatalogItemId(itemId);
    };

    useEffect(() => {
        const fromLink = catalogItemFromLink(`/?catalogo=${searchParams.get('catalogo') || ''}`);
        if (!fromLink) return;
        openCatalogItem(fromLink);
        setSearchParams({}, { replace: true });   // recarregar a página não reabre o item
    }, [searchParams]); // eslint-disable-line

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const drawerWidth = drawerOpen ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED;
    const current = items[Math.min(selected, items.length - 1)];

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar position="fixed" sx={{ backgroundColor: '#f77513', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar sx={{ gap: 2 }}>
                    <IconButton onClick={() => setDrawerOpen((o) => !o)} sx={{ color: 'white' }}>
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <BrandIcon ringColor="#1F6F4F" ringColorDark="#175A40" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    letterSpacing: { xs: '.1rem', sm: '.3rem' },
                                    fontSize: { xs: '1rem', sm: '1.25rem' },
                                    color: '#fff',
                                    lineHeight: 1.15
                                }}
                            >
                                re.colocarme
                            </Typography>
                            <Typography
                                variant="caption"
                                noWrap
                                sx={{ color: 'rgba(255,255,255,0.78)', fontWeight: 400, lineHeight: 1, fontSize: { xs: '0.6rem', sm: '0.68rem' } }}
                            >
                                aqui seu limão vira limonada
                            </Typography>
                        </Box>
                    </Box>
                    <Chip label="BACKOFFICE" size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 'bold' }} />
                    <Box sx={{ flexGrow: 1 }} />
                    {canSeeCatalog() && <AlertsBell onOpenItem={openCatalogItem} />}
                    <Typography variant="body2" sx={{ color: 'white' }}>
                        {getCurrentUsername()}
                    </Typography>
                    <Button onClick={handleLogout} startIcon={<LogoutIcon />} sx={{ color: 'white' }}>
                        Sair
                    </Button>
                </Toolbar>
            </AppBar>

            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        overflowX: 'hidden',
                        transition: (theme) => theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen
                        }),
                        boxSizing: 'border-box'
                    }
                }}
            >
                <Toolbar />
                <List sx={{ mt: 1 }}>
                    {items.map((item, index) => {
                        const isSelected = selected === index;
                        const button = (
                            <ListItemButton
                                onClick={() => setSelected(index)}
                                sx={{
                                    minHeight: 48,
                                    justifyContent: drawerOpen ? 'initial' : 'center',
                                    px: 2.5,
                                    borderLeft: isSelected ? '3px solid #f77513' : '3px solid transparent',
                                    backgroundColor: isSelected ? '#fff4ec' : 'transparent',
                                    '&:hover': { backgroundColor: '#fff4ec' }
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: drawerOpen ? 2 : 'auto',
                                        justifyContent: 'center',
                                        color: isSelected ? '#f77513' : 'text.secondary'
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    sx={{
                                        opacity: drawerOpen ? 1 : 0,
                                        '& .MuiListItemText-primary': {
                                            fontWeight: 'bold',
                                            fontSize: 13,
                                            color: isSelected ? '#f77513' : 'text.secondary'
                                        }
                                    }}
                                />
                            </ListItemButton>
                        );
                        return (
                            <Tooltip key={item.label} title={drawerOpen ? '' : item.label} placement="right">
                                {button}
                            </Tooltip>
                        );
                    })}
                </List>
            </Drawer>

            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Toolbar />
                {current ? current.render({ catalogItemId, clearCatalogItem: () => setCatalogItemId(null) }) : (
                    <Typography color="text.secondary">Seu usuário ainda não tem nenhum perfil. Peça a um administrador para atribuir um.</Typography>
                )}
            </Box>
        </Box>
    );
};

export default Dashboard;
